import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./IndexMoverPage.css";
import { Bar } from "react-chartjs-2";
import { API_BASE_URL } from '../config';
import RefreshButton from './RefreshButton';
import WatchlistStar from './WatchlistStar';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Title,
  Legend
} from "chart.js";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Title,
  Legend
);

const API_URL = `${API_BASE_URL}/api/analyze-indices`;

const IndexMoverPage = ({ onBack, onNiftyItClick }) => {
  const theme = 'dark';
  const [data, setData] = useState([]);
  const [activeTab, setActiveTab] = useState("");
  const [filterBy, setFilterBy] = useState("Change");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [chartData, setChartData] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  const getDisplaySymbol = useCallback((item) => {
    if (!item) return 'N/A';
    return (
      item.symbol ||
      item.ticker ||
      item.stock_symbol ||
      item.stock ||
      item.company ||
      'N/A'
    );
  }, []);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    onResize();
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const isPhone = viewportWidth <= 576;
  const isTinyPhone = viewportWidth <= 360;

  useEffect(() => {
    if (data.length > 0 && !activeTab) {
      setActiveTab(data[0]?.index || "");
    }
  }, [data, activeTab]);

  // Prepare chart data from constituents
  const prepareChartData = useCallback((constituents) => {
    if (!constituents || !constituents.length) return null;

    // Get all stocks and sort by change
    const sorted = [...constituents].sort((a, b) => (b.change || 0) - (a.change || 0));

    return {
      labels: sorted.map((stock) => getDisplaySymbol(stock)),
      datasets: [
        {
          data: sorted.map(stock => stock.change || 0),
          backgroundColor: sorted.map(stock => 
            (stock.change || 0) >= 0 ? '#10b981' : '#ef4444'
          ),
          borderColor: sorted.map(stock =>
            (stock.change || 0) >= 0 ? '#10b981' : '#ef4444'
          ),
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.8,
        }
      ]
    };
  }, [getDisplaySymbol]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}?_=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      const json = await res.json();
      if (!Array.isArray(json)) {
        throw new Error('Invalid data format: Expected an array');
      }
      const formattedData = json.map(section => ({
        ...section,
        constituents: Array.isArray(section.constituents) ? section.constituents : [],
        summary: {
          total_positive_change: section.summary?.total_positive_change || 0,
          total_negative_change: section.summary?.total_negative_change || 0,
          net_change: section.summary?.net_change || 0,
          ...section.summary
        }
      }));
      console.log('IndexMover sample constituent:', formattedData?.[0]?.constituents?.[0]);
      setData(formattedData);
      
      // Set chart data for the first index by default
      if (formattedData.length > 0 && formattedData[0].constituents) {
        setChartData(prepareChartData(formattedData[0].constituents));
      }
    } catch (err) {
      console.error('Error in fetchData:', err);
      setError(`Error loading data: ${err.message}`);
      setData([]);
      setChartData(null);
    } finally {
      setLoading(false);
    }
  }, [prepareChartData]);

  // Chart options
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: {
        display: false // Hide the legend since we're using color to indicate gain/loss
      },
      tooltip: {
        mode: "nearest",
        intersect: false,
        backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(93, 46, 169, 0.2)',
        titleColor: theme === 'dark' ? '#fff' : '#1a202c',
        bodyColor: theme === 'dark' ? '#e2e8f0' : '#4a5568',
        borderColor: theme === 'dark' ? '#4a5568' : '#e2e8f0',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.x || 0;
            return `${label}: ${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
          },
          labelColor: function(context) {
            return {
              borderColor: context.dataset.borderColor,
              backgroundColor: context.dataset.backgroundColor,
              borderWidth: 2,
              borderRadius: 4,
            };
          }
        }
      }
    },
    scales: {
      x: {
        display: false, // This will hide the x-axis completely
        grid: {
          display: false,
          drawBorder: false,
          drawTicks: false,
        },
        ticks: {
          display: false // Hide x-axis labels
        }
      },
      y: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
          font: { 
            size: 12,
            weight: '500'
          },
          padding: 8
        }
      }
    },
    layout: {
      padding: {
        left: 0,
        right: 0,
        top: 10,
        bottom: 0
      }
    },
    // Disable bar grow/shrink animations
    animation: false,
    barThickness: 16,
    maxBarThickness: 20,
    categoryPercentage: 0.8,
    barPercentage: 0.9,
  }), [theme]);

  // Update chart data when active tab changes
  useEffect(() => {
    if (data.length > 0 && activeTab) {
      const activeSection = data.find(section => section.index === activeTab);
      if (activeSection?.constituents) {
        setChartData(prepareChartData(activeSection.constituents));
      }
    }
  }, [activeTab, data, prepareChartData]);

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate positive and negative count from constituents
  const getPositiveNegativeCount = () => {
    if (data.length === 0 || !activeTab) return { positive: 0, negative: 0 };
    const section = data.find(s => s.index === activeTab);
    if (!section?.constituents) return { positive: 0, negative: 0 };
    
    const positive = section.constituents.filter(s => (s.change || 0) >= 0).length;
    const negative = section.constituents.filter(s => (s.change || 0) < 0).length;
    return { positive, negative };
  };

  const { positive: positiveCount, negative: negativeCount } = getPositiveNegativeCount();

  const mobileRowStyle = useMemo(
    () =>
      isPhone
        ? {
            display: 'grid',
            padding: '0.25rem 0',
            gap: '4px',
            gridTemplateColumns: isTinyPhone
              ? '1fr minmax(110px, 130px) 1fr'
              : '1fr minmax(130px, 160px) 1fr',
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
          }
        : undefined,
    [isPhone, isTinyPhone]
  );

  const mobileNameCellStyle = useMemo(
    () =>
      isPhone
        ? {
            fontSize: isTinyPhone ? '0.66rem' : '0.72rem',
            lineHeight: 1.15,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflow: 'visible',
            textOverflow: 'clip',
          }
        : undefined,
    [isPhone, isTinyPhone]
  );

  const mobileBarsContainerStyle = useMemo(
    () =>
      isPhone
        ? {
            height: '16px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }
        : undefined,
    [isPhone]
  );

  const mobileBarStyle = useMemo(
    () =>
      isPhone
        ? {
            height: '14px',
            borderRadius: '2px',
          }
        : undefined,
    [isPhone]
  );

  const mobileSymbolTextStyle = useMemo(
    () =>
      isPhone
        ? {
            fontSize: isTinyPhone ? '0.68rem' : '0.75rem',
            letterSpacing: isTinyPhone ? '0.2px' : '0.3px',
            fontWeight: 600,
          }
        : undefined,
    [isPhone, isTinyPhone]
  );

  const mobilePointsTextStyle = useMemo(
    () =>
      isPhone
        ? {
            display: 'block',
            fontSize: isTinyPhone ? '0.62rem' : '0.68rem',
            lineHeight: 1.05,
            marginLeft: 0,
          }
        : undefined,
    [isPhone, isTinyPhone]
  );

  const mobileGraphWrapperStyle = useMemo(
    () =>
      isPhone
        ? {
            width: '100%',
            maxWidth: '100%',
            marginLeft: 0,
            marginRight: 0,
            paddingLeft: 0,
            paddingRight: 0,
          }
        : undefined,
    [isPhone]
  );

  const mobileScrollableStyle = useMemo(
    () =>
      isPhone
        ? {
            width: '100%',
            maxWidth: '100%',
            marginLeft: 0,
            marginRight: 0,
            paddingLeft: 0,
            paddingRight: 0,
          }
        : undefined,
    [isPhone]
  );

  return (
    <div className="index-mover-page">
      <div className="header-actions">
        <div className="s4u-page-heading">
          <button className="back-button s4u-back-btn" onClick={onBack}>
            ← Back to Dashboard
          </button>
          <h2>Index Movers</h2>
        </div>
        <RefreshButton 
          onRefresh={fetchData} 
          isLoading={loading} 
          size="small"
          className="ml-2"
        />
      </div>

      {loading ? (
        <div className="loading">Loading market data...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div className="content">
          <div className="tabs">
            {data.map((section) => (
              <button
                key={section.index}
                className={`tab ${activeTab === section.index ? 'active' : ''}`}
                onClick={() => setActiveTab(section.index)}
              >
                {section.index}
              </button>
            ))}
          </div>

          {data
            .filter((section) => section.index === activeTab)
            .map((section) => {
              const isDenseLayout = (section.constituents?.length || 0) > 30;

              return (
              <div key={section.index} className="index-section">
                {/* Summary Statistics */}
                <div className="summary">
                  <h3>{section.index} Summary</h3>
                  <div className="summary-stats">
                    <div className="stat">
                      <span className="positive">
                        Gainers: {section.summary?.total_positive_change?.toFixed(2) || '0.00'}%
                      </span>
                      <span className="negative">
                        Losers: {Math.abs(section.summary?.total_negative_change || 0).toFixed(2)}%
                      </span>
                      <span className={(section.change !== undefined ? section.change : section.summary?.net_change) >= 0 ? 'positive' : 'negative'}>
                        Net: {(section.change !== undefined ? section.change : section.summary?.net_change || 0).toFixed(2)}%
                        {section.change_points !== undefined && (
                          <span style={{ fontSize: '0.9em', marginLeft: '4px', opacity: 0.9 }}>
                            ({section.change_points.toFixed(2)})
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Top Summary Bar with Badges */}
                <div className="summary-bar">
                  <div className="summary-badge positive-badge">
                    <span className="badge-label">Net Positive</span>
                    <span className="badge-value">({positiveCount})</span>
                  </div>
                  <h3 className="summary-title">Today Index Mover</h3>
                  <div className="summary-badge negative-badge">
                    <span className="badge-label">Net Negative</span>
                    <span className="badge-value">({negativeCount})</span>
                  </div>
                </div>

                {/* Diverging Bar Chart */}
                <div className="chart-container">
                  <div className="diverging-chart-wrapper" style={mobileGraphWrapperStyle}>
                    {section.constituents.length > 0 ? (
                      <div
                        className={`stocks-list-scrollable ${isDenseLayout ? 'stocks-list-dense' : ''}`}
                        style={mobileScrollableStyle}
                      >
                        {(() => {
                          const absChanges = section.constituents.map((item) => Math.abs(item.change || 0));
                          const absChangePoints = section.constituents.map((item) => Math.abs(item.change_points || 0));
                          const maxAbsChange = Math.max(...absChanges, 0.1);
                          const maxAbsChangePoints = Math.max(...absChangePoints, 0.1);

                          const positives = section.constituents
                            .filter((item) => (item.change || 0) >= 0)
                            .sort((a, b) => Math.abs(b.change || 0) - Math.abs(a.change || 0));
                          const negatives = section.constituents
                            .filter((item) => (item.change || 0) < 0)
                            .sort((a, b) => Math.abs(b.change || 0) - Math.abs(a.change || 0));

                          const maxRows = Math.max(positives.length, negatives.length);
                          const maxBarPercent = isPhone ? 50 : 48; // per side cap

                          return Array.from({ length: maxRows }).map((_, idx) => {
                            const pos = positives[idx];
                            const neg = negatives[idx];

                            const posChange = pos ? pos.change || 0 : 0;
                            const negChange = neg ? neg.change || 0 : 0;
                            const posChangePoints = pos ? pos.change_points || 0 : 0;
                            const negChangePoints = neg ? neg.change_points || 0 : 0;
                            const posAbs = Math.abs(posChange);
                            const negAbs = Math.abs(negChange);

                            const posPointsAbs = Math.abs(posChangePoints);
                            const negPointsAbs = Math.abs(negChangePoints);

                            // For mobile: use change_points if available (> 0), otherwise fall back to change %
                            const posPointsForWidth = posPointsAbs > 0 ? posPointsAbs : posAbs;
                            const negPointsForWidth = negPointsAbs > 0 ? negPointsAbs : negAbs;

                            // Calculate max using the same logic for consistency
                            const maxForWidth = Math.max(
                              ...section.constituents.map((item) =>
                                Math.abs(item.change_points || 0) > 0
                                  ? Math.abs(item.change_points || 0)
                                  : Math.abs(item.change || 0)
                              ),
                              0.1
                            );

                            const posWidth = pos
                              ? isPhone
                                ? (posPointsForWidth / maxForWidth) * 50
                                : Math.min((posAbs / maxAbsChange) * 100, maxBarPercent)
                              : 0;
                            const negWidth = neg
                              ? isPhone
                                ? (negPointsForWidth / maxForWidth) * 50
                                : Math.min((negAbs / maxAbsChange) * 100, maxBarPercent)
                              : 0;

                            const formatPoints = (v) => {
                              const n = parseFloat(v) || 0;
                              const sign = n >= 0 ? '+' : '';
                              return `${sign}${n.toFixed(2)}`;
                            };
                            const posSymbol = pos ? getDisplaySymbol(pos) : '';
                            const negSymbol = neg ? getDisplaySymbol(neg) : '';

                            return (
                              <div
                                key={idx}
                                className={isPhone ? 'stock-row-mobile' : 'stock-row'}
                                style={mobileRowStyle}
                              >
                                <div className="stock-name-left" style={mobileNameCellStyle}>
                                  {pos && (
                                    <>
                                      {!isPhone && (
                                        <WatchlistStar 
                                          stock={{
                                            symbol: getDisplaySymbol(pos),
                                            name: pos.company || pos.stock,
                                            change: pos.change,
                                            index: section.index
                                          }} 
                                          size="16px"
                                        />
                                      )}
                                      <span className="mobile-name" style={mobileSymbolTextStyle}>{posSymbol}</span>
                                      {isPhone && (
                                        <span className="mobile-points positive" style={mobilePointsTextStyle}> ({formatPoints(posChangePoints)})</span>
                                      )}
                                    </>
                                  )}
                                </div>

                                {!isPhone && (
                                  <div className="stock-percentage-left">
                                    {pos && <span className="percentage-text positive">{posChange.toFixed(2)}% <span style={{ fontSize: '0.8em', opacity: 0.8 }}>({posChangePoints.toFixed(2)})</span></span>}
                                  </div>
                                )}
                                <div className="bars-container" style={mobileBarsContainerStyle}>
                                  {pos && (
                                    <div
                                      className="bar positive"
                                      style={{ width: `${posWidth}%`, ...(mobileBarStyle || {}) }}
                                    >
                                      {!isPhone && <span className="bar-label">{posAbs.toFixed(2)}%</span>}
                                    </div>
                                  )}
                                  {neg && (
                                    <div
                                      className="bar negative"
                                      style={{ width: `${negWidth}%`, ...(mobileBarStyle || {}) }}
                                    >
                                      {!isPhone && <span className="bar-label">{negAbs.toFixed(2)}%</span>}
                                    </div>
                                  )}
                                </div>

                                {!isPhone && (
                                  <div className="stock-percentage-right">
                                    {neg && <span className="percentage-text negative">{negChange.toFixed(2)}% <span style={{ fontSize: '0.8em', opacity: 0.8 }}>({negChangePoints.toFixed(2)})</span></span>}
                                  </div>
                                )}
                                <div className="stock-name-right" style={mobileNameCellStyle}>
                                  {neg && (
                                    <>
                                      {!isPhone && (
                                        <WatchlistStar 
                                          stock={{
                                            symbol: getDisplaySymbol(neg),
                                            name: neg.company || neg.stock,
                                            change: neg.change,
                                            index: section.index
                                          }} 
                                          size="16px"
                                        />
                                      )}
                                      <span className="mobile-name" style={mobileSymbolTextStyle}>{negSymbol}</span>
                                      {isPhone && (
                                        <span className="mobile-points negative" style={mobilePointsTextStyle}> ({formatPoints(negChangePoints)})</span>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    ) : (
                      <div className="no-data">No data available</div>
                    )}
                  </div>
                </div>
              </div>
            );
            })}
        </div>
      )}
    </div>
  );
};

export default IndexMoverPage;